/**
 * AuditHistoryPanel - Zeigt die Änderungshistorie eines Datensatzes
 * 
 * Props:
 * - collectionName: Name der Collection (z.B. "work_time", "vacations")
 * - documentId: ID des Dokuments
 * - onClose: Optional - Callback zum Schließen
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { safeFormatDate, formatDateTimeBerlin } from '../lib/dateTime';
import {
  History,
  ChevronDown,
  ChevronUp,
  User,
  Clock,
  Edit2,
  Trash2,
  Plus,
  RotateCcw,
  Database,
  AlertTriangle,
  MessageSquare,
  ExternalLink,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';

// Feldnamen-Übersetzung Deutsch
const FIELD_TRANSLATIONS = {
  // Zeiterfassung
  hours: 'Stunden',
  start_time: 'Beginn',
  end_time: 'Ende',
  break_minutes: 'Pause (Min.)',
  date: 'Datum',
  description: 'Beschreibung',
  note: 'Notiz',
  notes: 'Notizen',
  wage_type: 'Lohnart',
  
  // Projekt-Zeit
  project_id: 'Auftrag',
  customer_id: 'Kunde',
  is_daily_rate: 'Tagespauschale',
  
  // Urlaub/Abwesenheit
  leave_type: 'Art',
  status: 'Status',
  start_date: 'Von',
  end_date: 'Bis',
  
  // Allgemein
  name: 'Name',
  email: 'E-Mail',
  first_name: 'Vorname',
  last_name: 'Nachname',
  role: 'Rolle',
  is_active: 'Aktiv',
  
  // Benutzer
  weekly_hours: 'Wochenstunden',
  vacation_days: 'Urlaubstage',
  hourly_rate: 'Stundensatz',
  daily_rate: 'Tagessatz',
  
  // Abrechnung
  billing_status: 'Abrechnungsstatus',
  invoice_number: 'Rechnungsnummer',
  total_amount: 'Gesamtbetrag',
  
  // Soft-Delete
  deleted_at: 'Gelöscht am',
  deleted_by: 'Gelöscht von',
  delete_reason: 'Löschgrund',
  
  // Kilometer
  km: 'Kilometer',
  distance_km: 'Entfernung (km)',
  
  // Genehmigung
  reviewed_at: 'Geprüft am',
  reviewed_by: 'Geprüft von',
  rejection_reason: 'Ablehnungsgrund',
};

// Aktions-Übersetzungen
const ACTION_LABELS = {
  create: { label: 'Angelegt', icon: Plus, color: 'bg-emerald-500/20 text-emerald-400' },
  update: { label: 'Geändert', icon: Edit2, color: 'bg-blue-500/20 text-blue-400' },
  delete: { label: 'Gelöscht', icon: Trash2, color: 'bg-red-500/20 text-red-400' },
  restore: { label: 'Wiederhergestellt', icon: RotateCcw, color: 'bg-purple-500/20 text-purple-400' },
  baseline: { label: 'Bestandsdaten', icon: Database, color: 'bg-muted text-muted-foreground' },
};

// Collection-Namen-Übersetzungen
const COLLECTION_LABELS = {
  work_time: 'Arbeitszeit',
  project_time: 'Auftragszeit',
  vacations: 'Abwesenheit',
  break_entries: 'Pausen',
  mileage_entries: 'Kilometer',
  overtime_ledger: 'Arbeitszeitkonto',
  invoices: 'Rechnungen',
  users: 'Benutzer',
  projects: 'Aufträge',
  customers: 'Kunden',
  approvals: 'Genehmigungen',
};

// Status-Übersetzungen
const STATUS_LABELS = {
  pending: 'Ausstehend',
  approved: 'Genehmigt',
  rejected: 'Abgelehnt',
  open: 'Offen',
  billed: 'Abgerechnet',
  paid: 'Bezahlt',
  cancelled: 'Storniert',
};

// Rollen-Übersetzungen
const ROLE_LABELS = {
  admin: 'Administrator',
  supervisor: 'Vorgesetzter',
  accounting: 'Buchhaltung',
  employee: 'Mitarbeiter',
  system: 'System',
};

/**
 * Übersetzt einen Feldnamen ins Deutsche
 */
const translateFieldName = (fieldName) => {
  return FIELD_TRANSLATIONS[fieldName] || fieldName;
};

/**
 * Formatiert einen Wert für die Anzeige
 */
const formatValue = (value, fieldName) => {
  if (value === null || value === undefined) return '—';
  if (value === true) return 'Ja';
  if (value === false) return 'Nein';
  
  // Status-Werte
  if (fieldName === 'status' && STATUS_LABELS[value]) {
    return STATUS_LABELS[value];
  }
  
  // Rollen
  if (fieldName === 'role' && ROLE_LABELS[value]) {
    return ROLE_LABELS[value];
  }
  
  // Datum/Zeit
  if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return safeFormatDate(value, 'd. MMM yyyy');
  }
  
  // ISO Timestamp
  if (typeof value === 'string' && value.includes('T') && value.includes(':')) {
    return formatDateTimeBerlin(value, "d. MMM yyyy, HH:mm 'Uhr'");
  }
  
  // Numerische Werte mit Einheiten
  if (typeof value === 'number') {
    if (fieldName === 'hours') return `${value.toFixed(2)} Std.`;
    if (fieldName === 'break_minutes') return `${value} Min.`;
    if (fieldName.includes('rate') || fieldName.includes('amount')) return `${value.toFixed(2)} €`;
    if (fieldName.includes('km') || fieldName === 'distance_km') return `${value} km`;
    return value.toString();
  }
  
  return String(value);
};

/**
 * Einzelner Änderungseintrag in der Timeline
 */
const AuditEntry = ({ entry, isFirst }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const actionConfig = ACTION_LABELS[entry.action] || ACTION_LABELS.update;
  const ActionIcon = actionConfig.icon;
  
  const timestamp = formatDateTimeBerlin(entry.timestamp, "d. MMM yyyy, HH:mm 'Uhr'", '—');
  
  const hasChanges = entry.changes && Object.keys(entry.changes).length > 0;
  const isForeignEdit = entry.is_foreign_edit;
  
  return (
    <div className={`relative pl-8 pb-6 ${!isFirst ? 'border-l border-border ml-3' : ''}`}>
      {/* Timeline Dot */}
      <div className={`absolute left-0 ${isFirst ? '-ml-0' : '-ml-3'} w-6 h-6 rounded-full flex items-center justify-center ${actionConfig.color}`}>
        <ActionIcon className="w-3 h-3" />
      </div>
      
      <div className={`ml-2 rounded-lg ${isForeignEdit ? 'border-l-2 border-amber-500 pl-3' : ''}`}>
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={actionConfig.color}>
                {actionConfig.label}
              </Badge>
              {isForeignEdit && (
                <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Fremdänderung
                </Badge>
              )}
            </div>
            
            <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
              <Clock className="w-3 h-3" />
              <span>{timestamp}</span>
            </div>
          </div>
          
          <div className="text-right text-sm">
            <div className="flex items-center gap-1 text-muted-foreground">
              <User className="w-3 h-3" />
              <span>{entry.user_name || 'Unbekannt'}</span>
            </div>
            <span className="text-xs text-muted-foreground">
              {ROLE_LABELS[entry.user_role] || entry.user_role}
            </span>
          </div>
        </div>
        
        {/* Änderungsgrund */}
        {entry.reason && (
          <div className="flex items-start gap-2 mb-3 p-2 bg-indigo-500/10 border border-indigo-500/20 rounded">
            <MessageSquare className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-indigo-700 dark:text-indigo-300">{entry.reason}</p>
          </div>
        )}
        
        {/* Änderungen */}
        {hasChanges && (
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-muted-foreground hover:text-foreground p-0 h-auto"
              >
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 mr-1" />
                ) : (
                  <ChevronDown className="w-4 h-4 mr-1" />
                )}
                {Object.keys(entry.changes).length} Änderung(en) anzeigen
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 space-y-1 text-sm">
                {Object.entries(entry.changes).map(([field, change]) => (
                  <div key={field} className="flex items-start gap-2 p-2 bg-muted/50 rounded">
                    <span className="text-muted-foreground min-w-[120px]">
                      {translateFieldName(field)}:
                    </span>
                    <span className="text-red-600 dark:text-red-400/70 line-through">
                      {formatValue(change.old, field)}
                    </span>
                    <span className="text-muted-foreground">→</span>
                    <span className="text-emerald-600 dark:text-emerald-400">
                      {formatValue(change.new, field)}
                    </span>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
        
        {/* Baseline Info */}
        {entry.action === 'baseline' && (
          <p className="text-xs text-muted-foreground mt-2">
            Dieser Eintrag wurde vor der Einführung des Audit-Trails erstellt.
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * AuditHistoryPanel Hauptkomponente
 */
export const AuditHistoryPanel = ({ 
  collectionName, 
  documentId, 
  isOpen = true,
  onToggle,
  compact = false,
}) => {
  const { api } = useAuth();
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(isOpen);

  const fetchHistory = useCallback(async () => {
    if (!collectionName || !documentId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/audit/history/${collectionName}/${documentId}`);
      setHistory(response.data || []);
    } catch (err) {
      console.error('Failed to fetch audit history:', err);
      setError('Änderungshistorie konnte nicht geladen werden.');
      setHistory([]);
    } finally {
      setIsLoading(false);
    }
  }, [api, collectionName, documentId]);

  useEffect(() => {
    if (isExpanded) {
      fetchHistory();
    }
  }, [fetchHistory, isExpanded]);

  const handleToggle = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    onToggle?.(newState);
  };

  const collectionLabel = COLLECTION_LABELS[collectionName] || collectionName;

  if (compact) {
    return (
      <Collapsible open={isExpanded} onOpenChange={handleToggle}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground gap-2"
          >
            <History className="w-4 h-4" />
            Änderungshistorie
            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-3 p-4 bg-card/50 rounded-lg border border-border">
            <HistoryContent 
              history={history} 
              isLoading={isLoading} 
              error={error} 
              collectionLabel={collectionLabel}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <div className="card-glass p-4">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={handleToggle}
      >
        <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
          <History className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          Änderungshistorie: {collectionLabel}
        </h3>
        <Button variant="ghost" size="sm" className="p-1">
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
      </div>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-4 pt-4 border-t border-border">
              <HistoryContent 
                history={history} 
                isLoading={isLoading} 
                error={error} 
                collectionLabel={collectionLabel}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Inhalt der Historie
 */
const HistoryContent = ({ history, isLoading, error, collectionLabel }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-600 dark:text-amber-400" />
        <p>{error}</p>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>Keine Änderungshistorie vorhanden</p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {history.map((entry, index) => (
        <AuditEntry 
          key={entry.id || index} 
          entry={entry} 
          isFirst={index === 0}
        />
      ))}
    </div>
  );
};

// Export der Übersetzungsfunktionen für andere Komponenten
export { 
  FIELD_TRANSLATIONS, 
  ACTION_LABELS, 
  COLLECTION_LABELS, 
  translateFieldName, 
  formatValue 
};

export default AuditHistoryPanel;
